import React, { useState, useCallback } from 'react'
import type { H2HStatsResponse, H2HFilter, H2HMatchStatsOut, TeamRollupOut } from '../types'
import { getTeamColorWithContrast } from '../utils/teamColors'
import { getLeagueBadge } from '../utils/leagueBadge'
import { HistoricalFixtureModal } from './match-analysis/components/HistoricalFixtureModal'

interface H2HMatchCardProps {
  match: H2HMatchStatsOut
  onFixtureClick?: (fixtureId: number) => void
}

function H2HMatchCard({ match, onFixtureClick }: H2HMatchCardProps) {
  const badge = getLeagueBadge(match.league_name)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onFixtureClick && match.api_fixture_id) {
      onFixtureClick(match.api_fixture_id)
    }
  }

  const isClickable = Boolean(onFixtureClick && match.api_fixture_id)

  return (
    <div
      className="h2h-match-card"
      onClick={isClickable ? handleClick : undefined}
      style={{
        cursor: isClickable ? 'pointer' : 'default',
        opacity: onFixtureClick && !match.api_fixture_id ? 0.6 : 1,
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleClick(e as unknown as React.MouseEvent) : undefined}
    >
      <div className="h2h-match-header">
        <span className="h2h-match-date">{match.date}</span>
        <div className="h2h-match-teams">
          <span className="h2h-match-team home">{match.home_team_name}</span>
          <span className="h2h-match-score">
            {match.home_score ?? '-'} - {match.away_score ?? '-'}
          </span>
          <span className="h2h-match-team away">{match.away_team_name}</span>
        </div>
        <span
          className="h2h-match-league"
          title={match.league_name || 'Unknown'}
          style={{ background: badge.color, color: badge.color === '#dae025' ? '#000' : '#fff' }}
        >
          {badge.code}
        </span>
        {isClickable && (
          <span className="h2h-match-arrow">→</span>
        )}
      </div>
    </div>
  )
}

interface StatRowProps {
  label: string
  home?: number | null
  away?: number | null
  suffix?: string
  decimal?: boolean
}

function StatRow({ label, home, away, suffix = '', decimal = false }: StatRowProps) {
  const formatValue = (val?: number | null) => {
    if (val === null || val === undefined) return '-'
    return decimal ? val.toFixed(1) : val.toString()
  }

  return (
    <div className="stat-row">
      <span className="stat-value">{formatValue(home)}{suffix && home != null ? suffix : ''}</span>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{formatValue(away)}{suffix && away != null ? suffix : ''}</span>
    </div>
  )
}

interface H2HTabProps {
  stats: H2HStatsResponse | null
  teams: TeamRollupOut[] | null
  filter: H2HFilter
  onFilterChange: (filter: H2HFilter) => void
}

export function H2HTab({ stats, teams: _teams, filter, onFilterChange }: H2HTabProps) {
  const [historicalFixtureId, setHistoricalFixtureId] = useState<number | null>(null)

  const handleFixtureClick = useCallback((fixtureId: number) => {
    setHistoricalFixtureId(fixtureId)
  }, [])

  const handleCloseModal = useCallback(() => {
    setHistoricalFixtureId(null)
  }, [])

  if (!stats) {
    return (
      <div className="small" style={{ marginTop: 10 }}>
        No H2H data available. Data is pre-cached for upcoming fixtures.
      </div>
    )
  }

  const { team1, team2, total_matches, team1_wins, draws, team2_wins, matches, averages, available_filters } = stats

  // Guard against missing data
  if (!team1 || !team2 || !averages) {
    return (
      <div className="small" style={{ marginTop: 10 }}>
        H2H data loading...
      </div>
    )
  }

  // Calculate win percentage bar widths
  const totalWins = team1_wins + team2_wins + draws
  const team1Pct = totalWins > 0 ? (team1_wins / totalWins) * 100 : 0
  const drawsPct = totalWins > 0 ? (draws / totalWins) * 100 : 0
  const team2Pct = totalWins > 0 ? (team2_wins / totalWins) * 100 : 0

  // Get team colors
  const team1Color = getTeamColorWithContrast(team1.name)
  const team2Color = getTeamColorWithContrast(team2.name)

  const filterLabels: Record<H2HFilter, string> = {
    last3: 'L3',
    last5: 'L5',
    last10: 'L10',
    all: 'All',
  }

  return (
    <div className="h2h-tab">
      {/* Filter chips - same style as Teams tab */}
      <div className="tab-filter-bar">
        {available_filters.map((f) => (
          <button
            key={f}
            className={`chip ${filter === f ? 'chip-active' : ''}`}
            onClick={() => onFilterChange(f)}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      {/* Summary card - same structure as Teams tab */}
      <div className="card stats-card">
        <div className="card-inner">
          {/* Team header row - matches TeamsTab exactly */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <span className="badge">
                {team1.crest_url ? <img src={team1.crest_url} alt="" /> : 'H'}
              </span>
              <div>
                <strong style={{ fontSize: 13 }}>{team1.name}</strong>
                <div className="small">{total_matches} matches</div>
              </div>
            </div>
            <div style={{ color: 'var(--muted)', fontWeight: 600, fontSize: 12 }}>vs</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ fontSize: 13 }}>{team2.name}</strong>
                <div className="small">{total_matches} matches</div>
              </div>
              <span className="badge">
                {team2.crest_url ? <img src={team2.crest_url} alt="" /> : 'A'}
              </span>
            </div>
          </div>

          {/* H2H Record row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>{team1_wins}W</span>
            <span style={{ fontWeight: 600, color: 'var(--muted)' }}>{draws}D</span>
            <span style={{ fontWeight: 600 }}>{team2_wins}W</span>
          </div>

          {/* Win bar */}
          <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ width: `${team1Pct}%`, background: team1Color.color, transition: 'width 0.3s' }} />
            <div style={{ width: `${drawsPct}%`, background: 'var(--muted)', transition: 'width 0.3s' }} />
            <div style={{ width: `${team2Pct}%`, background: team2Color.color, transition: 'width 0.3s' }} />
          </div>

          {/* H2H Averages - shows stats from H2H matches between these two teams */}
          <div className="small" style={{ textAlign: 'center', marginBottom: 8 }}>
            H2H Averages ({averages.matches} matches)
          </div>
          <div className="live-stats">
            <StatRow label="Goals" home={averages.team1_goals} away={averages.team2_goals} decimal />
            <StatRow label="Corners" home={averages.team1_corners} away={averages.team2_corners} decimal />
            <StatRow label="Shots" home={averages.team1_shots} away={averages.team2_shots} decimal />
            <StatRow label="On Target" home={averages.team1_shots_on_target} away={averages.team2_shots_on_target} decimal />
            <StatRow label="Possession" home={averages.team1_possession} away={averages.team2_possession} suffix="%" decimal />
            <StatRow label="Fouls" home={averages.team1_fouls} away={averages.team2_fouls} decimal />
            <StatRow label="Cards" home={averages.team1_cards} away={averages.team2_cards} decimal />
            {averages.team1_xg != null && averages.team2_xg != null && (
              <StatRow label="xG" home={averages.team1_xg} away={averages.team2_xg} decimal />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Goals</span>
              <span style={{ fontWeight: 600 }}>{averages.total_goals.toFixed(1)}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Corners</span>
              <span style={{ fontWeight: 600 }}>{averages.total_corners.toFixed(1)}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Cards</span>
              <span style={{ fontWeight: 600 }}>{averages.total_cards.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Match History */}
      <div className="h2h-history">
        <div className="h2h-history-header">
          <span>Match History</span>
          <span className="h2h-history-count">{total_matches} total</span>
        </div>
        <div className="h2h-history-list">
          {matches.map((match, idx) => (
            <H2HMatchCard key={match.api_fixture_id ?? `match-${idx}`} match={match} onFixtureClick={handleFixtureClick} />
          ))}
        </div>
      </div>

      {/* Historical Fixture Modal */}
      {historicalFixtureId !== null && (
        <HistoricalFixtureModal
          fixtureId={historicalFixtureId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
